// @ts-check
import { useEffect, useState } from 'react';

export const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

/**
 * Render the Google Identity Services sign-in button into `ref` and call
 * `onCredential(response)` with the ID-token response. Used by super-admin
 * sign-in and by super-admin factor enrolment, which needs a FRESH Google
 * sign-in as its identity proof (FDNA-02b D-b1). Returns whether the button
 * is ready. Does nothing when no client id is configured.
 *
 * @param {import('react').RefObject<HTMLElement | null>} ref
 * @param {(response: { credential: string }) => void} onCredential
 * `nonce`, when given, is bound into the ID token Google issues, so the server
 * can tell this sign-in from any earlier one. `enabled: false` waits (e.g.
 * until that nonce has been fetched).
 *
 * @param {{ text?: string, width?: number, nonce?: string, enabled?: boolean }} [opts]
 * @returns {boolean}
 */
export function useGoogleSignInButton(
  ref,
  onCredential,
  { text = 'signin_with', width = 356, nonce, enabled = true } = {}
) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !enabled) return undefined;
    let cancelled = false;
    /** @type {ReturnType<typeof setTimeout> | null} */
    let retryTimer = null;
    const w = /** @type {any} */ (window);

    function initGsi() {
      if (cancelled) return;
      if (!w.google?.accounts?.id) {
        retryTimer = setTimeout(initGsi, 100);
        return;
      }
      w.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: onCredential,
        auto_select: false,
        ...(nonce ? { nonce } : {}),
      });
      if (ref.current) {
        w.google.accounts.id.renderButton(ref.current, {
          type: 'standard',
          theme: 'filled_black',
          size: 'large',
          width,
          text,
          shape: 'rectangular',
        });
      }
      setReady(true);
    }

    if (document.getElementById('google-gsi-script')) {
      initGsi();
    } else {
      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initGsi;
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [ref, onCredential, text, width, nonce, enabled]);

  return ready;
}
